import { Controller } from "stimulus";
import Rails from "@rails/ujs";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = ['input', 'checkbox'];
  static values = {
    url: String
  }

  connect() {
    this.intializeParams();

    this.filter = debounce(this.filter.bind(this), 300);

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("input", this.filter);
    }

    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach(cb => cb.addEventListener("click", this.filter));
    }

    document.addEventListener("promote:checkbox-select-all", this.filter);
    document.addEventListener("promote:checkbox-deselect-all", this.filter);
  }

  disconnect() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("input", this.filter);
    }

    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach(cb => cb.removeEventListener("click", this.filter));
    }

    document.removeEventListener("promote:checkbox-select-all", this.filter);
    document.removeEventListener("promote:checkbox-deselect-all", this.filter);
  }

  intializeParams() {
    this.params = {};

    this.params.selectedItems = new Set();
    this.params.search = "";

    if (this.hasCheckboxTarget) {
      const filterNames = new Set(
        this.checkboxTargets.map(checkbox => checkbox.dataset.filterName)
      );

      filterNames.forEach(name => {
        if (name) this.params[name] = [];
      });
    }
  }

  toggleSelection(event) {
    if (event.target.checked) {
      this.params.selectedItems.add(event.target.value);
    } else {
      this.params.selectedItems.delete(event.target.value);
    }
  }

  filter() {
    this._collectParams();
    this._toggleCloseIcon();
    this._loadData();
  }

  resetOnClick() {
    if (this.hasInputTarget) {
      this.inputTarget.value = '';
    }

    this._collectParams();
    this._toggleCloseIcon();
    this._loadData();
  }

  _syncCheckboxes() {
    this.element.querySelectorAll('input[type="checkbox"][name="source_ids[]"]')
      .forEach(checkbox => {
        const id = checkbox.value;
        checkbox.checked = this.params.selectedItems.has(id);
      });
  }

  _collectParams() {
    this.params.search = this.hasInputTarget ? this.inputTarget.value.trim() : "";

    Object.keys(this.params).forEach(key => {
      if (Array.isArray(this.params[key])) this.params[key] = [];
    });

    if (this.hasCheckboxTarget) {
      this.checkboxTargets.forEach(checkbox => {
        if (!checkbox.checked) return;

        const filterName = checkbox.dataset.filterName;
        const value = checkbox.closest('[data-value]')?.dataset.value;

        if (!filterName || !value) return;

        this.params[filterName].push(value);
      });
    }
  }

  _toggleCloseIcon() {
    if (!this.hasCloseIconTarget) return;

    if (this.inputTarget?.value.trim()) {
      this.closeIconTarget.classList.remove("hidden");
    } else {
      this.closeIconTarget.classList.add("hidden");
    }
  }

  _loadData() {
    if (this.ajaxRequest) {
      this.ajaxRequest.abort();
    }

    Rails.ajax({
      url: this._buildUrl(),
      type: "GET",
      dataType: "script",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      },
      success: (response) => {
        this._syncCheckboxes();
      },
    });
  }

  _buildUrl() {
    if (!this.urlValue) return "";

    const searchParams = new URLSearchParams();

    Object.entries(this.params).forEach(([key, value]) => {
      if (Array.isArray(value) || value instanceof Set) {
        const arrayValue = Array.isArray(value) ? value : Array.from(value);

        if (arrayValue.length === 0) {
          searchParams.append(`${key}[]`, "");
        } else {
          arrayValue.forEach(v => searchParams.append(`${key}[]`, v));
        }
      } else  {
        searchParams.set(key, value);
      }
    });

    if ([...searchParams].length === 0) return this.urlValue;

    const separator = this.urlValue.includes("?") ? "&" : "?";

    return this.urlValue + separator + searchParams.toString();
  }
}
